<template>
    <el-dialog v-model="visible" title="群组选择/机器人简易管理" :close-on-click-modal="false" :close-on-press-escape="false"
        :show-close="false" :width="dialogWidth" class="group-selector-dialog">

        <!-- 添加退出按钮 -->
        <template #header>
            <div class="dialog-header">
                <span class="dialog-title">群组选择/机器人简易管理</span>
                <el-button type="danger" size="small" @click="handleLogout" class="logout-btn" :icon="Logout">
                    退出登录
                </el-button>
            </div>
        </template>

        <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="main-tabs">
            <el-tab-pane label="群组列表" name="groups">
                <div class="group-selector-content">
                    <div class="selector-header">
                        <el-icon class="header-icon">
                            <ChatDotRound />
                        </el-icon>
                        <div class="header-text">
                            <h3>请选择您要管理的群组</h3>
                            <p>💡群状态显示活跃代表机器人在群内拥有起码的管理权限，如果状态显示禁用，那么机器人可能被群主踢出群，或者机器人没有最起码的管理权限</p>
                        </div>
                    </div>

                    <div class="group-list">
                        <el-table :data="groups" style="width: 100%;" @row-click="handleGroupSelect"
                            :row-class-name="getRowClassName" v-loading="loading" border>
                            <el-table-column prop="groupid" label="群组ID" min-width="120" />
                            <el-table-column prop="grouptitle" label="群组名称" min-width="120" />
                            <el-table-column prop="type" label="类 型" min-width="100" />
                            <el-table-column prop="status" label="状态" min-width="80">
                                <template #default="{ row }">
                                    <el-tag
                                        :type="row.status === 1 ? 'success' : row.status === 2 ? 'warning' : 'danger'"
                                        size="small">
                                        {{ row.status === 1 ? '活跃' : row.status === 2 ? '备份' : '禁用' }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" min-width="150" fixed="right">
                                <template #default="{ row }">
                                    <el-button type="primary" size="small" @click.stop="handleGroupSelect(row)">
                                        选择
                                    </el-button>
                                    <el-button type="danger" size="small" @click.stop="handleGroupDelete(row)">
                                        删除
                                    </el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>

                    <div class="selector-footer">
                        <el-alert title="⚠️提示：如果列表中没有群组，那么您需要先切换到【机器人列表】页，先创建机器人，然后让机器人加入到群组中并授权，刷新当前页面，群组将会自动出现在列表中"
                            type="info" :closable="false" show-icon />
                    </div>
                </div>
            </el-tab-pane>

            <el-tab-pane label="机器人列表" name="bots">
                <div class="bot-manager-content">
                    <div class="toolbar">
                        <el-button type="success" @click="showAddBotDialog" size="large">
                            <el-icon>
                                <Plus />
                            </el-icon>新增机器人
                        </el-button>
                    </div>
                    <el-table :data="bots" style="width: 100%" v-loading="botsLoading" border>
                        <el-table-column prop="botname" label="BOT用户名" min-width="180" />
                        <el-table-column prop="token" label="Token" min-width="250" show-overflow-tooltip />
                        <el-table-column prop="status" label="状态" min-width="100">
                            <template #default="scope">
                                <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                                    {{ scope.row.status === 1 ? '已运行' : '未运行' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="createdAt" label="创建时间" min-width="160" sortable>
                            <template #default="scope">{{ formatDateTime(scope.row.createdAt) }}</template>
                        </el-table-column>
                        <el-table-column label="操作" min-width="120" fixed="right">
                            <template #default="scope">
                                <el-button type="success" size="small" @click="handleStartBot(scope.row)">
                                    <el-icon>
                                        <Refresh />
                                    </el-icon>启动/重启
                                </el-button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <div class="selector-footer">
                        <el-alert
                            title="⚠️提示：请私聊@botfather创建机器人，并获取Token，@username 然后输入到【新增机器人】的表单中，点击【确定】按钮即可创建机器人，新建成功之后请点击【启动/重启】按钮启动机器人，确保【状态】显示为【已运行】之后，请把机器人拉入群内，设置为管理员，最少要赋予删除消息 踢人的权限"
                            type="info" :closable="false" show-icon />
                    </div>

                </div>
            </el-tab-pane>
        </el-tabs>
    </el-dialog>

    <!-- 新增机器人弹窗 -->
    <el-dialog v-model="botDialogVisible" title="新增机器人" width="600px" :close-on-click-modal="false" destroy-on-close>
        <el-form ref="botFormRef" :model="newBot" :rules="botRules" label-width="100px">
            <el-form-item label="BOT用户名" prop="botname">
                <el-input v-model="newBot.botname" placeholder="请输入机器人用户名（@开头）" />
            </el-form-item>
            <el-form-item label="Token" prop="token">
                <el-input v-model="newBot.token" placeholder="请输入机器人Token" />
            </el-form-item>
        </el-form>
        <template #footer>
            <div class="dialog-footer">
                <el-button @click="botDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitNewBot">确定</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { ChatDotRound, Plus, Refresh, Logout } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import api from '@/services/api'

export default {
    name: 'GroupSelector',
    components: {
        ChatDotRound,
        Plus,
        Refresh
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            groups: [],
            loading: false,
            selectedGroupId: null,
            activeTab: 'groups',
            bots: [],
            botsLoading: false,
            botsLoaded: false, // 标记机器人数据是否已加载
            botDialogVisible: false,
            newBot: {
                botname: '',
                token: ''
            },
            botRules: {
                botname: [{ required: true, message: '请输入机器人用户名（@开头）', trigger: 'blur' }],
                token: [{ required: true, message: '请输入机器人Token', trigger: 'blur' }],
            }
        }
    },
    computed: {
        visible: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)
            }
        },
        dialogWidth() {
            // 获取浏览器窗口宽度
            const windowWidth = window.innerWidth;
            console.log('windowWidth', windowWidth);

            // 根据屏幕宽度动态调整弹窗宽度
            if (windowWidth < 768) {
                // 移动端：90%宽度
                return '90%';
            } else if (windowWidth < 1200) {
                // 平板：80%宽度
                return '80%';
            } else if (windowWidth < 1600) {
                // 小桌面：70%宽度
                return '70%';
            } else {
                // 大桌面：60%宽度，避免过宽
                return '60%';
            }
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                // 重置状态
                this.activeTab = 'groups';
                this.botsLoaded = false;
                this.bots = [];
                // 默认加载群组
                this.loadGroups()
            }
        }
    },
    mounted() {
        // 监听窗口大小变化
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        // 移除监听器
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleTabClick(tab) {
            if (tab.paneName === 'bots' && !this.botsLoaded) {
                this.loadBots();
            }
        },
        handleResize() {
            // 强制重新计算弹窗宽度
            this.$forceUpdate();
        },
        async loadGroups() {
            this.loading = true
            try {
                // 从用户信息中获取电报 ID
                const currentUser = this.$store.state.user;
                const telegramUserId = currentUser?.telegram?.id;

                if (!telegramUserId) {
                    this.$message.error('无法获取用户电报 ID');
                    this.loading = false; // 修正：在出错时停止加载状态
                    return;
                }

                // 使用项目现有的 API 调用方式，传递用户电报 ID
                const response = await api.getGroups(telegramUserId);
                this.groups = response.data;

                // 通知父组件群组数据已加载
                this.$emit('groups-loaded', this.groups);
            } catch (error) {
                console.error('加载群组失败:', error);
                this.$message.error('加载群组失败');
            } finally {
                this.loading = false;
            }
        },

        // --- Bot Management Methods ---
        getOwnerId() {
            const username = this.$store.state.user?.username || '';
            const match = username.match(/^tg_(\d{5,20})$/);
            return match ? match[1] : '';
        },

        async loadBots() {
            this.botsLoading = true;
            try {
                const ownerid = this.getOwnerId();
                if (!ownerid) {
                    this.$message.error('无法获取用户信息以加载机器人列表');
                    return;
                }
                const params = {
                    page: 1, // 简化，不分页
                    pageSize: 1000, // 获取足够多的数据
                    ownerid: ownerid,
                };
                const response = await api.getBots(params);
                if (response.data.code === 0) {
                    this.bots = response.data.data.records;
                    this.botsLoaded = true; // 标记已加载
                } else {
                    this.$message.error(response.data.message || '获取机器人列表失败');
                }
            } catch (error) {
                console.error('获取机器人列表失败:', error);
                this.$message.error('获取机器人列表失败');
            } finally {
                this.botsLoading = false;
            }
        },

        showAddBotDialog() {
            this.newBot = { botname: '', token: '' };
            this.botDialogVisible = true;
        },

        async submitNewBot() {
            const form = this.$refs.botFormRef;
            if (!form) return;

            try {
                await form.validate();
                const ownerid = this.getOwnerId();
                if (!ownerid) {
                    this.$message.error('无法获取当前用户ownerid');
                    return;
                }

                const response = await api.createBot({ ...this.newBot, ownerid });

                if (response.data.code === 0) {
                    this.$message.success('创建成功');
                    this.botDialogVisible = false;
                    // 重新加载机器人列表
                    this.loadBots();
                } else {
                    this.$message.error(response.data.message || '操作失败');
                }

            } catch (error) {
                if (error) { // 捕获校验失败
                    console.error('表单校验或提交失败:', error);
                    // El-form的校验失败会抛出错误，无需额外提示
                }
            }
        },

        formatDateTime(dateTime) {
            if (!dateTime) return '-';
            const date = new Date(dateTime);
            return date.toLocaleString('zh-CN');
        },

        async handleGroupSelect(group) {
            this.selectedGroupId = group.groupid;

            // 只需本地存储 groupid，无需调用 API
            localStorage.setItem('groupId', group.groupid.toString());

            // console.log('group', JSON.stringify(group));

            // 更新 store 中的用户信息
            const currentUser = this.$store.state.user;
            if (currentUser) {
                const updatedUser = {
                    ...currentUser,
                    groupId: group.groupid,
                    userBot: group.userBot,
                    userId: group.userId
                };
                this.$store.commit('setUser', updatedUser);
            }

            // 关闭弹窗
            this.visible = false;

            // 通知父组件群组已选择
            this.$emit('group-selected', group);

            // this.$message.success(`已选择群组：${group.grouptitle}`);
        },

        getRowClassName({ row }) {
            return row.groupid === this.selectedGroupId ? 'selected-row' : ''
        },

        handleGroupsLoaded(groups) {
            // 接收来自 GroupSelector 的群组数据
            this.groups = groups;
            console.log('群组数据已加载:', groups);
        },

        async handleGroupDelete(group) {
            try {
                await ElMessageBox.confirm(
                    `您确定要删除群组 "${group.grouptitle}" 吗？此操作将永久删除该群组在本系统中的记录，但不会影响您在 Telegram 中的实际群组。`,
                    '警告', {
                    confirmButtonText: '确定删除',
                    cancelButtonText: '取消',
                    type: 'warning',
                }
                );

                // 调用API删除
                // 修正：使用 group.originalId 作为数据库主键
                await api.deleteGroup(group.originalId);

                this.$message.success('群组删除成功！');

                // 重新加载列表
                this.loadGroups();

            } catch (error) {
                // 用户点击取消时，ElMessageBox会抛出 'cancel' 字符串
                if (error !== 'cancel') {
                    console.error('删除群组失败:', error);
                    const errorMessage = error.response?.data?.message || '删除群组失败，请稍后重试。';
                    this.$message.error(errorMessage);
                }
            }
        },

        async handleStartBot(bot) {
            try {
                const response = await api.startBot(bot.id);
                if (response.data.code === 0) {
                    this.$message.success('启动/重启成功');
                    // 重新加载机器人列表
                    this.loadBots();
                } else {
                    this.$message.error(response.data.message || '启动/重启失败');
                }
            } catch (error) {
                console.error('启动/重启机器人失败:', error);
                this.$message.error('启动/重启机器人失败');
            }
        },

        handleLogout() {
            // 清除用户信息并跳转到登录页面
            this.$store.commit('setUser', null);
            this.visible = false;
            this.$router.push('/telegram-login');
        }
    }
}
</script>

<style scoped>
.group-selector-dialog :deep(.el-dialog) {
    border-radius: 12px;
}

.group-selector-dialog :deep(.el-dialog__header) {
    background: linear-gradient(135deg, #0088cc 0%, #00a8ff 100%);
    color: white;
    border-radius: 12px 12px 0 0;
    padding: 0;
    /* 移除默认padding，由自定义header控制 */
}

.group-selector-dialog :deep(.el-dialog__title) {
    color: white;
    font-size: 18px;
    font-weight: 600;
}

.group-selector-dialog :deep(.el-dialog__body) {
    padding: 0;
}

.main-tabs :deep(.el-tabs__nav-wrap) {
    padding: 0 24px;
}

.main-tabs :deep(.el-tabs__header) {
    margin-bottom: 0;
}

.group-selector-content {
    max-height: calc(70vh - 55px);
    /* 减去tabs的高度 */
    overflow-y: auto;
}

.bot-manager-content {
    padding: 20px 24px;
    max-height: calc(70vh - 55px);
    /* 减去tabs的高度 */
    overflow-y: auto;
}

.toolbar {
    margin-bottom: 16px;
}

.selector-header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
}

.header-icon {
    font-size: 24px;
    color: #0088cc;
    margin-right: 16px;
}

.header-text h3 {
    margin: 0 0 4px 0;
    font-size: 16px;
    color: #333;
}

.header-text p {
    margin: 0;
    font-size: 14px;
    color: #666;
}

.group-list {
    padding: 20px 24px;
    overflow-x: auto;
}

.group-list :deep(.selected-row) {
    background-color: #f0f9ff;
}

.group-list :deep(.selected-row td) {
    background-color: #f0f9ff !important;
}

.selector-footer {
    padding: 16px 24px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
}

.selector-footer :deep(.el-alert) {
    margin: 0;
    border: none;
    background: transparent;
}

.selector-footer :deep(.el-alert__title) {
    font-size: 13px;
    color: #666;
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: linear-gradient(135deg, #0088cc 0%, #00a8ff 100%);
    color: white;
    border-radius: 12px 12px 0 0;
}

.dialog-title {
    font-size: 18px;
    font-weight: 600;
    color: white;
}

.logout-btn {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
}

.logout-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
}
</style>